import React from "react";
import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Typography
} from "@mui/material";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../common/CommonLoader/CommonLoader";
import AppButton from "../../common/AppButton";
import {
  useEducationSettings,
  useUpdateEducationSettings
} from "../../../hooks/api/education/useEducationSettings";
import type { EducationSettings } from "./types/educationSettings";

const EducationSettingsPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { data: settings, isPending } = useEducationSettings();
  const updateMutation = useUpdateEducationSettings();
  const [draft, setDraft] = React.useState<EducationSettings | null>(null);

  React.useEffect(() => {
    if (settings) {
      setDraft(settings);
    }
  }, [settings]);

  if (isPending || draft == null) {
    return (
      <CommonLoader
        loadingMessage={t("loading-education-settings", "Loading settings...")}
      />
    );
  }

  const toggle = (key: keyof EducationSettings) => {
    setDraft((prev) => (prev ? { ...prev, [key]: !prev[key] } : prev));
  };

  const handleSave = async () => {
    await updateMutation.mutateAsync({
      expectedVersion: draft.version,
      settings: {
        billInactiveStudents: draft.billInactiveStudents,
        blockPromotionIfOutstandingFees: draft.blockPromotionIfOutstandingFees,
        allowOverpayment: draft.allowOverpayment,
        allowNegativeStock: draft.allowNegativeStock,
        requireApprovalForWriteOff: draft.requireApprovalForWriteOff,
        requireApprovalForPaymentReversal:
          draft.requireApprovalForPaymentReversal,
        noMatchingFeeBlocksBilling: draft.noMatchingFeeBlocksBilling,
        paymentAllowedForWithdrawnStudents:
          draft.paymentAllowedForWithdrawnStudents
      }
    });
  };

  return (
    <Box data-testid="education-settings-page" sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        {t("education-settings-title", "Education settings")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t(
          "education-settings-subtitle",
          "School-wide billing, payment, and inventory policies."
        )}
      </Typography>

      <Stack spacing={1}>
        <FormControlLabel
          control={
            <Switch
              checked={!!draft.billInactiveStudents}
              onChange={() => toggle("billInactiveStudents")}
            />
          }
          label={t("settings-bill-inactive-students", "Bill inactive students")}
        />
        <FormControlLabel
          control={
            <Switch
              checked={!!draft.blockPromotionIfOutstandingFees}
              onChange={() => toggle("blockPromotionIfOutstandingFees")}
            />
          }
          label={t(
            "settings-block-promotion-fees",
            "Block promotion when fees are outstanding"
          )}
        />
        <FormControlLabel
          control={
            <Switch
              checked={draft.allowOverpayment !== false}
              onChange={() => toggle("allowOverpayment")}
            />
          }
          label={t(
            "settings-allow-overpayment",
            "Allow overpayment on invoices"
          )}
        />
        <FormControlLabel
          control={
            <Switch
              checked={!!draft.allowNegativeStock}
              onChange={() => toggle("allowNegativeStock")}
            />
          }
          label={t("settings-allow-negative-stock", "Allow negative stock")}
        />
        <FormControlLabel
          control={
            <Switch
              checked={draft.requireApprovalForWriteOff !== false}
              onChange={() => toggle("requireApprovalForWriteOff")}
            />
          }
          label={t(
            "settings-require-writeoff-approval",
            "Require approval for write-offs"
          )}
        />
        <FormControlLabel
          control={
            <Switch
              checked={draft.requireApprovalForPaymentReversal !== false}
              onChange={() => toggle("requireApprovalForPaymentReversal")}
            />
          }
          label={t(
            "settings-require-reversal-approval",
            "Require approval for payment reversals"
          )}
        />
        <FormControlLabel
          control={
            <Switch
              checked={draft.noMatchingFeeBlocksBilling !== false}
              onChange={() => toggle("noMatchingFeeBlocksBilling")}
            />
          }
          label={t(
            "settings-no-fee-blocks-billing",
            "Block billing when no fee structure matches"
          )}
        />
        <FormControlLabel
          control={
            <Switch
              checked={draft.paymentAllowedForWithdrawnStudents !== false}
              onChange={() => toggle("paymentAllowedForWithdrawnStudents")}
            />
          }
          label={t(
            "settings-payment-withdrawn-students",
            "Allow payments from withdrawn students"
          )}
        />
      </Stack>

      <AppButton
        sx={{ mt: 3 }}
        disabled={updateMutation.isPending}
        onClick={handleSave}
      >
        {t("save-settings", "Save settings")}
      </AppButton>
    </Box>
  );
};

export default EducationSettingsPage;
